import { useState } from "react";
import axios from "axios";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Phone, Mail, MapPin, Clock, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", interest: "candidate", message: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", interest: "candidate", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail || "Something went wrong. Please call or email us instead."
      );
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact"
        description="Let's start a real conversation. Call 0274 382 028 or email Bronwyn@uniterecruitment.co.nz — we answer the phone."
        image="/og-brand.png"
        path="/contact"
      />
      <PageHero
        overline="Contact us"
        title="Let's start a real conversation."
        accentWord="real"
        subtitle="Drop us a line below, or reach out directly — whichever feels easier."
      />

      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <aside className="lg:col-span-5" data-testid="contact-details">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">Reach us directly</p>
            <h2 className="font-headings text-3xl md:text-4xl text-ink tracking-tight leading-tight mb-8">
              We answer the phone. <span className="hand-underline active">Really.</span>
            </h2>

            <ul className="space-y-7 font-body">
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-ink text-mint flex items-center justify-center flex-shrink-0">
                  <Phone size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-dark mb-1">Phone</p>
                  <a href="tel:+64274382028" data-testid="contact-phone-link" className="font-headings text-2xl text-ink hover:text-stone-dark transition-colors">
                    0274 382 028
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-ink text-mint flex items-center justify-center flex-shrink-0">
                  <Mail size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-dark mb-1">Email</p>
                  <a href="mailto:Bronwyn@uniterecruitment.co.nz" data-testid="contact-email-link" className="font-headings text-2xl text-ink hover:text-stone-dark transition-colors break-all">
                    Bronwyn@uniterecruitment.co.nz
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-ink text-mint flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-dark mb-1">Location</p>
                  <p className="font-headings text-2xl text-ink">Aotearoa, New Zealand</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-ink text-mint flex items-center justify-center flex-shrink-0">
                  <Clock size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-dark mb-1">Hours</p>
                  <p className="font-headings text-2xl text-ink">Mon – Fri · 8:30am – 5:30pm NZT</p>
                </div>
              </li>
            </ul>
          </aside>

          <div className="lg:col-span-7" data-testid="contact-form-card">
            <div className="bg-white rounded-2xl p-8 md:p-12 border border-ink-100">
              {status === "success" ? (
                <div data-testid="contact-form-success" className="flex flex-col items-center text-center py-12">
                  <CheckCircle2 size={56} className="text-stone-dark mb-6" strokeWidth={1.5} />
                  <h3 className="font-headings text-3xl text-ink mb-3">Thanks — we'll be in touch.</h3>
                  <p className="font-body text-ink-700 max-w-md">
                    A real human will read your message and get back to you within one working day.
                  </p>
                  <button
                    data-testid="contact-form-reset"
                    onClick={() => setStatus("idle")}
                    className="mt-8 inline-flex items-center gap-2 bg-ink text-mint px-6 py-3 rounded-full font-body font-medium text-sm"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-3">Quick form</p>
                  <h3 className="font-headings text-3xl md:text-4xl text-ink mb-2 leading-tight">Tell us a little.</h3>
                  <p className="font-body text-ink-700 mb-8">We'll respond personally within one working day.</p>

                  <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                    <div>
                      <label htmlFor="name" className="block text-xs uppercase tracking-[0.2em] text-stone-dark font-medium mb-2">Your name</label>
                      <input
                        id="name" name="name" type="text" required value={form.name} onChange={onChange}
                        data-testid="contact-input-name"
                        className="w-full bg-cream border border-ink-100 rounded-xl px-5 py-3.5 font-body text-ink placeholder:text-ink-700/50 focus:outline-none focus:border-ink"
                        placeholder="Sam Taylor"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs uppercase tracking-[0.2em] text-stone-dark font-medium mb-2">Email</label>
                      <input
                        id="email" name="email" type="email" required value={form.email} onChange={onChange}
                        data-testid="contact-input-email"
                        className="w-full bg-cream border border-ink-100 rounded-xl px-5 py-3.5 font-body text-ink placeholder:text-ink-700/50 focus:outline-none focus:border-ink"
                        placeholder="sam@company.co.nz"
                      />
                    </div>
                    <div>
                      <label htmlFor="interest" className="block text-xs uppercase tracking-[0.2em] text-stone-dark font-medium mb-2">I'm interested in</label>
                      <select
                        id="interest" name="interest" value={form.interest} onChange={onChange}
                        data-testid="contact-input-interest"
                        className="w-full bg-cream border border-ink-100 rounded-xl px-5 py-3.5 font-body text-ink focus:outline-none focus:border-ink"
                      >
                        <option value="candidate">Finding a job</option>
                        <option value="employer">Hiring talent</option>
                        <option value="general">Just a chat</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-xs uppercase tracking-[0.2em] text-stone-dark font-medium mb-2">Message</label>
                      <textarea
                        id="message" name="message" rows={5} required value={form.message} onChange={onChange}
                        data-testid="contact-input-message"
                        className="w-full bg-cream border border-ink-100 rounded-xl px-5 py-3.5 font-body text-ink placeholder:text-ink-700/50 focus:outline-none focus:border-ink resize-none"
                        placeholder="Tell us a bit about what you're looking for…"
                      />
                    </div>

                    {status === "error" && (
                      <p data-testid="contact-form-error" className="text-sm text-red-700 font-body">{errorMsg}</p>
                    )}

                    <button
                      type="submit" disabled={status === "loading"} data-testid="contact-form-submit"
                      className="inline-flex items-center justify-center gap-2 bg-ink text-mint hover:bg-stone-dark disabled:opacity-50 px-8 py-4 rounded-full font-body font-medium transition-transform hover:-translate-y-0.5 w-full sm:w-auto"
                    >
                      {status === "loading" ? (
                        <><Loader2 size={18} className="animate-spin" /> Sending…</>
                      ) : (
                        <>Send message →</>
                      )}
                    </button>

                    <p className="text-xs text-ink-700/70 font-body pt-2">
                      By submitting you agree to our <a href="/privacy" className="underline">privacy policy</a>. This form can be swapped for a HubSpot embed at any time.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
